// Simple guest cart stored in localStorage
// Structure of each item:
// {
//   _id: string, // unique key productId[-ticketId][-variantId]
//   productId: {
//     _id: string,
//     productName: string,
//     sellingPrice: number,
//     price: number,
//     productImage: string[],
//     category: string
//   },
//   quantity: number,
//   ticketType?: { id: string, name: string, price: string|number, description?: string }
//   variantId?: string,       // V2 variant selection (Phase 4)
//   skuId?: string,
//   variantLabel?: string,    // e.g. "STORAGE: 256GB, COLOR: Blue"
// }

const STORAGE_KEY = 'guest_cart_v1'

export function getGuestCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed
    return []
  } catch (_) {
    return []
  }
}

export function saveGuestCart(items) {
  // Cross-component sync used to happen via a `guest-cart-updated` CustomEvent that the
  // header listened for. Header now reads from Redux's cartSlice instead, and the slice's
  // guest-action thunks (addGuestItemAction, updateGuestQtyAction, …) mirror the new
  // items into Redux after this write. Direct callers that don't dispatch (e.g. the
  // login-flow `clearGuestCart` after merging) only mutate localStorage; the next mount
  // re-hydrates state from this same key.
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items || []))
  } catch (_) {}
}

function makeItemId(productId, ticketType, variantInfo) {
  // Include ticket + variant in the key so different picks land as
  // separate cart lines (adding 256GB Blue and 512GB Black stays as
  // two rows). Variant takes precedence over the raw productId when
  // building the suffix.
  const parts = [productId]
  if (ticketType && ticketType.id) parts.push(String(ticketType.id))
  if (variantInfo && variantInfo.variantId) parts.push(String(variantInfo.variantId))
  return parts.join('-')
}

export function countGuestCart() {
  return getGuestCart().reduce((sum, it) => sum + (Number(it.quantity) || 0), 0)
}

export function clearGuestCart() {
  saveGuestCart([])
}

export function removeGuestItemById(itemId) {
  const items = getGuestCart().filter(it => it._id !== itemId)
  saveGuestCart(items)
  return items
}

export function updateGuestQty(itemId, quantity) {
  const items = getGuestCart()
  const idx = items.findIndex(it => it._id === itemId)
  if (idx >= 0) {
    items[idx].quantity = Math.max(1, Number(quantity) || 1)
    saveGuestCart(items)
  }
  return items
}

// Merge the guest cart into the authenticated server cart. Called from the
// login flows (email + Google) AFTER the auth cookie is set and BEFORE
// navigating away, so the user lands on a cart that already contains what
// they picked as a guest.
//
// Behaviour:
//   * every item is POSTed to /addtocart with its full shape (ticketType,
//     variantId, skuId, variantLabel, store) — same as an authed add
//   * items that merge successfully are removed from the guest cart
//   * items that FAIL stay in localStorage so nothing is silently lost;
//     the caller gets { merged, failed } to decide what to tell the user
export async function mergeGuestCartToServer() {
  const guestItems = getGuestCart()
  if (!Array.isArray(guestItems) || guestItems.length === 0) {
    return { merged: 0, failed: 0 }
  }

  const SummaryApi = (await import('../common')).default

  let storeCode = null
  try {
    const getSelectedStoreCode = (await import('./getSelectedStoreCode')).default
    storeCode = getSelectedStoreCode()
  } catch (_) {}

  const results = await Promise.all(guestItems.map(async (item) => {
    const body = {
      productId: item?.productId?._id || item?.productId || item?._id,
      quantity: item?.quantity || 1,
      ...(item?.ticketType ? { ticketType: item.ticketType } : {}),
      ...(item?.variantId ? { variantId: item.variantId } : {}),
      ...(item?.skuId ? { skuId: item.skuId } : {}),
      ...(item?.variantLabel ? { variantLabel: item.variantLabel } : {}),
      ...(storeCode ? { store: String(storeCode).toUpperCase() } : {}),
    }
    try {
      const res = await fetch(SummaryApi.addToCartProduct.url, {
        method: SummaryApi.addToCartProduct.method,
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      return { item, ok: res.ok && json?.success === true }
    } catch (_) {
      return { item, ok: false }
    }
  }))

  const failedItems = results.filter(r => !r.ok).map(r => r.item)
  saveGuestCart(failedItems)

  // Mirror into Redux so the header badge is correct immediately.
  try {
    const { store } = await import('../store/store')
    const { setGuestItems, fetchServerCartCount } = await import('../store/cartSlice')
    store.dispatch(setGuestItems(failedItems))
    store.dispatch(fetchServerCartCount())
  } catch (_) {}

  return { merged: results.length - failedItems.length, failed: failedItems.length }
}

export async function addGuestItem(productSnapshot, quantity = 1, ticketType = null, variantInfo = null) {
  const items = getGuestCart()
  const _id = makeItemId(productSnapshot._id, ticketType, variantInfo)
  const existingIdx = items.findIndex(it => it._id === _id)

  if (existingIdx >= 0) {
    items[existingIdx].quantity += Number(quantity) || 1
  } else {
    // Variant-aware price: prefer the resolved variant's price when the
    // caller picked one — matches the PDP's price display + prevents
    // the checkout total from silently reverting to the base product
    // price if the customer picked a differently-priced variant.
    const variantSellingPrice = variantInfo?.sellingPrice
    const variantListPrice    = variantInfo?.price
    items.push({
      _id,
      productId: {
        _id: productSnapshot._id,
        productName: productSnapshot.productName,
        sellingPrice: Number(variantSellingPrice ?? productSnapshot.sellingPrice) || 0,
        price: Number(variantListPrice ?? productSnapshot.price ?? variantSellingPrice ?? productSnapshot.sellingPrice) || 0,
        productImage: Array.isArray(productSnapshot.productImage) ? productSnapshot.productImage : [productSnapshot.productImage].filter(Boolean),
        category: productSnapshot.category || ''
      },
      quantity: Number(quantity) || 1,
      ...(ticketType ? { ticketType } : {}),
      // Phase 4 of the variant/SKU UX rework: preserve variant
      // selection across the guest → authed cart merge. Server-side
      // login-flow merge reads variantId + skuId from these fields
      // and hands them to /addtocart with the same shape as an authed
      // add-to-cart call.
      ...(variantInfo ? {
        variantId: variantInfo.variantId || undefined,
        skuId: variantInfo.skuId || undefined,
        variantLabel: variantInfo.label || variantInfo.variantLabel || undefined,
      } : {})
    })
  }

  saveGuestCart(items)
  return items
}
